import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaFutbol, FaTrophy, FaUsers, FaPlay, FaChartLine, FaMedal, FaCrown } from 'react-icons/fa';
import { achievements } from './data/achievements';
import founderImage from './images/about-the-founder.jpg';
import logo from './images/logo-long.svg';
import MatchCard from './components/match/MatchCard';
// import clarityService from './services/clarityService'; // Disabled - tracking removed for App Store compliance
import './scss/academyWelcomePage.scss';

function FriendlyWelcomePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [clickedAchievement, setClickedAchievement] = useState(null);
  
  // Get team ID from URL params
  const searchParams = new URLSearchParams(location.search);
  const teamId = searchParams.get('team_id') || '1';
  const teamName = searchParams.get('team_name') || 'Your Friendly Team';
  const organiserName = searchParams.get('organiser_name') || 'Organiser';
  
  // Team details
  const teamLogo = `https://football.pebbledesigns.co.uk/api/images/teams/${teamId}/logo-dark.png`;
  
  // Add body class for this page
  useEffect(() => {
    document.body.classList.add('academy-welcome-page');
    
    // Clarity tracking disabled - removed for App Store compliance
    // clarityService.trackEvent('friendly_welcome_page_view', {
    //   teamId: teamId,
    //   teamName: teamName
    // });
    
    return () => {
      document.body.classList.remove('academy-welcome-page');
    };
  }, [teamId, teamName]);
  
  // Sample match data for preview
  const sampleMatches = [
    {
      match_id: 'demo-future',
      match_date: '2024-12-15',
      match_time: '14:00:00',
      location: 'Local Park',
      opposition: 'Sunday League Rivals',
      entry_open: 1,
      max_players: 8,
      total_yes: 6,
      total_no: 1,
      total_awaiting: 1,
      active_yes: 6,
      waitlist_yes: 0,
      is_home: 1,
      match_type: 'friendly',
      response: null,
      team_name: teamName
    },
    {
      match_id: 'demo-past',
      match_date: '2024-12-01',
      match_time: '14:00:00',
      location: 'Community Ground',
      opposition: 'Local Legends',
      entry_open: null,
      max_players: 8,
      total_yes: 7,
      total_no: 1,
      total_awaiting: 0,
      active_yes: 7,
      waitlist_yes: 0,
      is_home: 0,
      match_type: 'friendly',
      response: 'Yes',
      score_white: 4,
      score_colour: 3,
      motm: 'demo-player',
      team_name: teamName,
      scorers: {
        'White': [
          { name: 'John D', goals: 2 },
          { name: 'Mike R', goals: 1 },
          { name: 'Tom B', goals: 1 }
        ],
        'Colour': [
          { name: 'Alex K', goals: 2 },
          { name: 'Sam P', goals: 1 }
        ]
      }
    },
    {
      match_id: 'demo-future-2',
      match_date: '2024-12-22',
      match_time: '15:30:00',
      location: 'Recreation Ground',
      opposition: 'Weekend Warriors',
      entry_open: 1,
      max_players: 8,
      total_yes: 5,
      total_no: 2,
      total_awaiting: 1,
      active_yes: 5,
      waitlist_yes: 0,
      is_home: 0,
      match_type: 'friendly',
      response: null,
      team_name: teamName
    }
  ];
  
  // Achievement data for preview
  const previewAchievements = [
    { id: 'iron_man', isAchieved: true, isLatest: true },
    { id: 'hat_trick', isAchieved: true, isLatest: false },
    { id: 'mvp', isAchieved: false, isLatest: false },
    { id: 'hot_streak', isAchieved: false, isLatest: false },
    { id: 'first_strike', isAchieved: true, isLatest: false },
    { id: 'star_performer', isAchieved: false, isLatest: false }
  ];
  
  const handleGetStarted = () => {
    setIsLoading(true);
    
    // Clarity tracking disabled - removed for App Store compliance
    // clarityService.trackEvent('friendly_welcome_cta_click', {
    //   teamId: teamId,
    //   teamName: teamName,
    //   buttonText: 'Ready to Start?'
    // });
    
    // Navigate to team-specific HTML page (for iOS home screen compatibility)
    setTimeout(() => {
      window.location.href = `/${teamId}.html`;
    }, 300);
  };

  const handleAchievementClick = (achievementId) => {
    setClickedAchievement(achievementId);
    
    // Clarity tracking disabled - removed for App Store compliance
    // const achievementData = achievements.find(a => a.id === achievementId);
    // clarityService.trackEvent('achievement_click', {
    //   achievementId: achievementId,
    //   achievementTitle: achievementData?.title || 'Unknown',
    //   teamId: teamId,
    //   teamName: teamName
    // });
    
    // Reset after animation
    setTimeout(() => {
      setClickedAchievement(null);
    }, 1000);
  };

  return (
    <div className="academy-welcome-page">
      <Helmet>
        <title>{`${teamName} - FirstWhistle Friendly Team Welcome`}</title>
        <meta name="description" content={`Welcome to ${teamName}! Join FirstWhistle to track player stats, achievements, and keep your friendly team organized.`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:title" content={`${teamName} - FirstWhistle Friendly Team Welcome`} />
        <meta property="og:description" content={`Welcome to ${teamName}! Join FirstWhistle to track player stats, achievements, and keep your friendly team organized.`} />
        <meta property="og:image" content={teamLogo} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="FirstWhistle" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={window.location.href} />
        <meta name="twitter:title" content={`${teamName} - FirstWhistle Friendly Team Welcome`} />
        <meta name="twitter:description" content={`Welcome to ${teamName}! Join FirstWhistle to track player stats, achievements, and keep your friendly team organized.`} />
        <meta name="twitter:image" content={teamLogo} />
        
        {/* Additional meta tags for better SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="author" content="FirstWhistle" />
        <meta name="keywords" content={`${teamName}, friendly football, player stats, achievements, FirstWhistle`} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      <div className="welcome-container">
        {/* Header with Team Name */}
        <div className="academy-header">
          <div className="logo-container">
            <img 
              src={teamLogo} 
              alt={`${teamName} logo`} 
              className="academy-logo"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <div className="logo-placeholder" style={{ display: 'none', background: 'none', border: 'none', boxShadow: 'none' }}>
              <img src={logo} alt="FirstWhistle Logo" style={{ width: '120px', height: '120px' }} />
            </div>
          </div>
                              <h1 className="academy-name">{organiserName}, Welcome to FirstWhistle</h1>
        </div>

        {/* FOMO Section */}
        <div className="fomo-section">
          <div className="fomo-badge">
            <span className="fomo-icon">🎉</span>
            <span className="fomo-text">Your team has been added!</span>
          </div>
          
          {/* Personal Message from Dean */}
          <div className="dean-message">
            <div className="dean-avatar">
              <img src={founderImage} alt="Dean - FirstWhistle Founder" />
            </div>
            <div className="dean-content">
              <h4>Hi, I'm Dean</h4>
              <p>As a football organiser myself, I know how much fun friendly games can be. That's why I created FirstWhistle to make organizing your team easier and more enjoyable.</p>
            </div>
          </div>
          
          <h2 className="fomo-title">Make organising your team easier than ever</h2>
          <p className="fomo-subtitle">Manage availability, choose balanced teams, and enjoy the gameplay of FIFA in real life</p>
        </div>

        {/* Benefits Grid - Friendly Team Focused */}
        <div className="benefits-section">
          <div className="benefits-grid">
            <div className="benefit-card">
              <div className="benefit-icon">
                <FaChartLine />
              </div>
              <h3>AI Team Generation</h3>
              <p>Automatically generate balanced teams based on player stats and form</p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <FaTrophy />
              </div>
              <h3>FIFA-Style Profiles</h3>
              <p>Players get their own profile with stats, achievements, and golden boot leaderboard</p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <FaMedal />
              </div>
              <h3>Auto Form Calculation</h3>
              <p>Player form automatically calculated based on match performance and POTM awards</p>
            </div>
            
            <div className="benefit-card">
              <div className="benefit-icon">
                <FaCrown />
              </div>
              <h3>Golden Boot Race</h3>
              <p>Track goals scored and compete for the golden boot throughout the season</p>
            </div>
          </div>
        </div>

        {/* Achievement Preview */}
        <div className="achievement-preview-section">
          <h3 className="preview-title">See Achievements in Action</h3>
          <div className="achievement-grid">
            {previewAchievements.map((previewAchievement) => {
              const achievementData = achievements.find(a => a.id === previewAchievement.id);
              if (!achievementData) return null;
              
              return (
                <div 
                  key={previewAchievement.id}
                  className={`achievement-coin ${previewAchievement.isAchieved ? 'achieved' : 'locked'} ${clickedAchievement === previewAchievement.id ? 'clicked' : ''}`}
                  onClick={() => handleAchievementClick(previewAchievement.id)}
                >
                  {previewAchievement.isLatest && (
                    <div className="latest-badge">Latest!</div>
                  )}
                  <div className="coin-content">
                    <div className="award-icon">
                      <div className={`award-card ${previewAchievement.isAchieved ? 'achieved' : 'locked'}`}>
                        {achievementData.icon}
                      </div>
                    </div>
                    <div className="award-info">
                      <h3>{achievementData.title}</h3>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="achievement-description">
            This is just an example. Players earn many more achievement coins including Goal Machine, Playmaker, Defensive Rock, and special awards given at match reports.
          </p>
        </div>

        {/* Social Proof */}
        <div className="social-proof-section">
          <div className="social-proof-card">
            <div className="quote-icon">"</div>
            <p className="testimonial">
              "I love seeing my stats and achievements! It adds competitiveness for all people of all abilities and keeps everyone motivated."
            </p>
            <div className="testimonial-author">
              <div className="author-avatar">M</div>
              <div className="author-info">
                <h4>Mark, Age 28</h4>
                <span>Friendly Team Player</span>
              </div>
            </div>
          </div>
        </div>

        {/* Setup Steps */}
        <div className="setup-steps-section">
          <h3 className="setup-title">Getting Started is Easy</h3>
          <div className="setup-steps">
            <div className="setup-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Click Ready to Start</h4>
                <p>Your team profile is ready to go - use mobile number and password which has been sent to you in a message or email</p>
              </div>
            </div>
            <div className="setup-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Add Players</h4>
                <p>Quickly add your team roster with player details and stats</p>
              </div>
            </div>
            <div className="setup-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Start Playing</h4>
                <p>Create matches, track stats, and watch your team's achievements grow</p>
              </div>
            </div>
          </div>
          <p className="setup-description">
            You are then ready to add matches, track player stats, generate balanced teams, and award achievements!
          </p>
        </div>

        {/* CTA Button */}
        <div className="cta-section">
          <button 
            className={`cta-button ${isLoading ? 'loading' : ''}`}
            onClick={handleGetStarted}
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="loading-text">Loading...</span>
            ) : (
              <>
                <span>Ready to Start?</span>
                <FaPlay className="play-icon" />
              </>
            )}
          </button>
          <p className="cta-subtitle">Join friendly teams already using FirstWhistle</p>
        </div>

        {/* Contact Details */}
        <div className="contact-section">
          <h3>Questions?</h3>
          <p>Get in touch with Dean for any questions about FirstWhistle</p>
          <div className="contact-details">
            <div className="contact-item">
              <span className="contact-label">Email:</span>
              <a href="mailto:dean.hodges@firstwhistle.app" className="contact-link">dean.hodges@firstwhistle.app</a>
            </div>
            <div className="contact-item">
              <span className="contact-label">Phone:</span>
              <a href="tel:07875930733" className="contact-link">07875 930733</a>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="welcome-footer">
          <p>Powered by FirstWhistle</p>
        </footer>
      </div>
    </div>
  );
}

export default FriendlyWelcomePage; 